import { useEffect } from "react";

import { useOpfsWorker } from "~/features/cache/opfs-context";
import {
  CIRCULARS_QUERY,
  dayPlanQuery,
  departureQuery,
} from "~/features/query-cache/queries";
import { useRootData } from "~/hooks/use-root-data";
import { circularsQueryFn } from "~/routes/api.circulars";
import { dayPlanQueryFn } from "~/routes/api.day-plan";
import { departureQueryFn } from "~/routes/api.departure";
import { EVENTS_QUERY_KEY, eventsQueryFn } from "~/routes/api.events";
import {
  CHECKLIST_QUERY_KEY,
  checklistsQueryFn,
} from "~/routes/api.resources.train-info.checklist";
import {
  VEHICLE_SETS_QUERY_KEY,
  vehicleSetsQueryFn,
} from "~/routes/api.vehicle-sets";
import { useDocumentQueries } from "~/routes/documents/hooks";
import { typedBoolean } from "~/utils/typing";
import { resolveEmployeeNumber } from "~/utils/user";

import type { UseQueryOptions } from "@tanstack/react-query";
import { useQueries } from "@tanstack/react-query";
import { formatISO } from "date-fns";
import { toMilliseconds } from "duration-fns";

/**
 * Component that doesn't render anything, but prefetches data for the app.
 *
 * Must be used inside a `QueryClientProvider`, but as high up in the React tree as possible.
 */
export const PrefetchQueries: React.FC = () => {
  const documentQueries = useDocumentQueries();

  const rootData = useRootData();

  const [dayPlan, circulars, trainOperationDocuments] = useQueries({
    queries: [
      {
        queryFn: dayPlanQueryFn,
        notifyOnChangeProps: ["data"],
        ...dayPlanQuery(
          resolveEmployeeNumber(
            rootData?.user?.employeeId,
            rootData?.dayPlanOverride,
          ),
          rootData?.dayPlanOverride?.date,
        ),
      },
      {
        queryFn: circularsQueryFn,
        ...CIRCULARS_QUERY,
        notifyOnChangeProps: ["data"],
        refetchIntervalInBackground: true,
        refetchInterval: toMilliseconds({
          minutes: 10,
        }),
      },
      {
        ...documentQueries.trainOperationDocuments,
        notifyOnChangeProps: ["data"],
        refetchIntervalInBackground: true,
        refetchInterval: toMilliseconds({
          minutes: 10,
        }),
      },
      {
        ...documentQueries.fidoReserveDocuments,
        notifyOnChangeProps: [],
      },
      {
        queryFn: eventsQueryFn,
        notifyOnChangeProps: ["data"],
        queryKey: EVENTS_QUERY_KEY,
        refetchIntervalInBackground: true,
        refetchInterval: toMilliseconds({
          minutes: 10,
        }),
      },
      {
        queryFn: checklistsQueryFn,
        notifyOnChangeProps: ["data"],
        queryKey: CHECKLIST_QUERY_KEY,
        staleTime: toMilliseconds({
          days: 1,
        }),
      },
      {
        queryFn: vehicleSetsQueryFn,
        notifyOnChangeProps: ["data"],
        queryKey: VEHICLE_SETS_QUERY_KEY,
        gcTime: Infinity,
        staleTime: toMilliseconds({
          days: 1,
        }),
      },
    ],
  });

  const opfs = useOpfsWorker();

  // TODO: Do this in global onSuccess if possible
  useEffect(() => {
    if (circulars.data) {
      opfs.postWorkerMessage({
        type: "CACHE_CIRCULARS",
        payload: circulars.data,
      });
    }
  }, [circulars.data, opfs]);

  // TODO: Do this in global onSuccess if possible
  useEffect(() => {
    if (trainOperationDocuments.data) {
      opfs.postWorkerMessage({
        type: "CACHE_TRAIN_OPERATION_DOCUMENTS",
        payload: trainOperationDocuments.data,
      });
    }
  }, [trainOperationDocuments.data, opfs]);

  useQueries({
    queries: dayPlan.data
      ? Object.values(dayPlan.data)
          .flatMap((it) => it.dutyAggregates)
          .flatMap((it) => it.taskAggregates)
          .map(({ trainId, nominalDate }) => {
            if (trainId && nominalDate) {
              const departure = {
                trainId,
                nominalDate: new Date(nominalDate),
              };

              return {
                queryFn: departureQueryFn(
                  new URLSearchParams({
                    trainId,
                    date: formatISO(departure.nominalDate, {
                      representation: "date",
                    }),
                  }),
                ),
                ...departureQuery(departure),
              } satisfies UseQueryOptions;
            }
            return null;
          })
          .filter(typedBoolean)
      : [],
  });

  return null;
};
